/**
 * Tiny cross-component pub/sub for the calculator's assist surfaces.
 *
 * ParseTextbox detects the LLM's `wantsGarmentSuggestion` flag and
 * dispatches `open-suggest` — AskAiDrawer listens for it and opens
 * itself pre-running the suggest query. Avoids threading a
 * controlled-drawer prop through three layers of components just for
 * this one cross-cutting affordance.
 *
 * Custom DOM events are the right fit here: the two components share a
 * DOM tree (both under <ScreenPrintQuotePage>) and the payload is
 * parameterless (or a small known shape). If this grows more use
 * cases, promote to a small React context.
 */
export const CALC_ASSIST_EVENTS = {
  openSuggest: 'fsp.calc.assist.open-suggest',
  openRecovery: 'fsp.calc.assist.open-recovery',
} as const;

export type OpenSuggestDetail = {
  /** Category hint from parse ("tee" / "hoodie"). Omit to search all. */
  category?: 'tee' | 'hoodie';
};

export function dispatchOpenSuggest(detail: OpenSuggestDetail = {}): void {
  window.dispatchEvent(
    new CustomEvent<OpenSuggestDetail>(CALC_ASSIST_EVENTS.openSuggest, { detail }),
  );
}

export function subscribeOpenSuggest(handler: (detail: OpenSuggestDetail) => void): () => void {
  const wrapped = (e: Event) => {
    const ev = e as CustomEvent<OpenSuggestDetail>;
    handler(ev.detail ?? {});
  };
  window.addEventListener(CALC_ASSIST_EVENTS.openSuggest, wrapped);
  return () => window.removeEventListener(CALC_ASSIST_EVENTS.openSuggest, wrapped);
}

export type OpenRecoveryDetail = {
  /** Category hint for the garment-swap lever. */
  category?: 'tee' | 'hoodie';
};

export function dispatchOpenRecovery(detail: OpenRecoveryDetail = {}): void {
  window.dispatchEvent(
    new CustomEvent<OpenRecoveryDetail>(CALC_ASSIST_EVENTS.openRecovery, { detail }),
  );
}

export function subscribeOpenRecovery(handler: (detail: OpenRecoveryDetail) => void): () => void {
  const wrapped = (e: Event) => {
    const ev = e as CustomEvent<OpenRecoveryDetail>;
    handler(ev.detail ?? {});
  };
  window.addEventListener(CALC_ASSIST_EVENTS.openRecovery, wrapped);
  return () => window.removeEventListener(CALC_ASSIST_EVENTS.openRecovery, wrapped);
}
